import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus } from "lucide-react";
import { Reveal } from "./Reveal";

const faqs = [
  {
    q: "How does the photo estimate work?",
    a: "Snap a few photos of whatever's in the way. Our system reads the items, estimates the volume and labor, and returns an honest price — usually in seconds. No measuring, no guessing.",
  },
  {
    q: "What happens to my stuff?",
    a: "We're donation-first. Anything with life left in it goes to local charities and reuse centers. The rest is recycled responsibly. Landfill is always the last resort.",
  },
  {
    q: "Are there any hidden fees?",
    a: "None. The price you see in your estimate is the price you pay. No fuel surcharges, no per-item add-ons, no surprises at the door.",
  },
  {
    q: "Do I need to be home for the pickup?",
    a: "For inside pickups, someone should be present to point us to the items. For curbside or outdoor pickups, you don't need to be there at all.",
  },
  {
    q: "How soon can you come?",
    a: "Often same-week, sometimes same-day depending on your area. You'll choose a window that works for you when you book.",
  },
  {
    q: "What areas do you serve?",
    a: "We're expanding quickly across major metros. Enter your ZIP during your estimate and we'll confirm availability instantly.",
  },
];

export default function FAQ() {
  const [open, setOpen] = useState(0);

  return (
    <section className="py-24 sm:py-32 lg:py-40">
      <div className="mx-auto max-w-4xl px-5 sm:px-8">
        <Reveal>
          <p className="text-sm font-semibold uppercase tracking-[0.2em] text-violet/70">Questions</p>
        </Reveal>
        <Reveal delay={0.05}>
          <h2 className="mt-5 font-display text-4xl leading-[1.02] tracking-tight text-charcoal sm:text-5xl lg:text-6xl text-balance">
            Good to know.
          </h2>
        </Reveal>

        <div className="mt-14">
          {faqs.map((f, i) => {
            const isOpen = open === i;
            return (
              <Reveal key={f.q} delay={i * 0.04}>
                <div className="border-t border-stone/70 last:border-b">
                  <button
                    onClick={() => setOpen(isOpen ? -1 : i)}
                    data-testid={`faq-${i}`}
                    className="flex w-full items-center justify-between gap-6 py-7 text-left"
                  >
                    <span className="font-display text-2xl text-charcoal sm:text-3xl">{f.q}</span>
                    <span
                      className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full transition-all duration-300 ${
                        isOpen ? "bg-violet text-white rotate-45" : "bg-white/70 text-charcoal"
                      }`}
                    >
                      <Plus size={18} />
                    </span>
                  </button>
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                        className="overflow-hidden"
                      >
                        <p className="max-w-2xl pb-8 text-lg leading-relaxed text-charcoal/60">{f.a}</p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}
